import React from "react";
import { toast } from "sonner";
import { Printer, MessageCircle, Copy, Zap } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const crmUrl = () => `${window.location.origin}/login`;

const message = (u) =>
  `Welcome to Connect Tym CRM, ${u.name}!\n\nRole: ${u.role}\nLogin ID: ${u.login_id}\nLogin here: ${crmUrl()}\n\nYour password will be shared separately by the Super Admin. Please keep your login private.`;

const waLink = (u) => {
  const digits = (u.mobile || "").replace(/\D/g, "");
  const phone = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${phone}?text=${encodeURIComponent(message(u))}`;
};

const esc = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const printCard = (u) => {
  const w = window.open("", "_blank", "width=520,height=640");
  if (!w) { toast.error("Allow pop-ups to print the card"); return; }
  w.document.write(`<html><head><title>Welcome Card - ${esc(u.name)}</title><style>
    body{font-family:Arial,sans-serif;display:flex;justify-content:center;padding:32px;color:#0f172a}
    .c{width:360px;border:2px solid #2563eb;border-radius:16px;overflow:hidden}
    .h{background:#2563eb;color:#fff;padding:18px 20px}.h b{font-size:20px}.h p{margin:4px 0 0;font-size:12px;opacity:.85}
    .b{padding:20px}.r{margin-bottom:14px}.l{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#64748b}
    .v{font-size:16px;font-weight:bold;margin-top:2px;word-break:break-all}.id{font-family:monospace;font-size:22px;color:#2563eb}
    .n{font-size:11px;color:#64748b;border-top:1px dashed #cbd5e1;padding-top:12px}
    .q{text-align:center;margin:6px 0 16px}.q svg{width:150px;height:150px}.q p{font-size:11px;color:#64748b;margin:6px 0 0}</style></head><body>
    <div class="c"><div class="h"><b>Connect Tym CRM</b><p>Staff Welcome Card</p></div><div class="b">
    <div class="r"><div class="l">Name</div><div class="v">${esc(u.name)}</div></div>
    <div class="r"><div class="l">Role</div><div class="v">${esc(u.role)}${u.designation ? " · " + esc(u.designation) : ""}</div></div>
    <div class="r"><div class="l">Login ID</div><div class="v id">${esc(u.login_id)}</div></div>
    <div class="r"><div class="l">Login at</div><div class="v">${esc(crmUrl())}</div></div>
    <div class="q">${document.getElementById("welcome-qr")?.outerHTML || ""}<p>Scan to open the login page</p></div>
    <div class="n">Your password is shared separately by the Super Admin. Never share your login with anyone.</div>
    </div></div><script>window.onload=function(){window.print()}</script></body></html>`);
  w.document.close();
};

export function WelcomeCardDialog({ user, onClose }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(message(user)); toast.success("Welcome message copied"); }
    catch (e) { toast.error("Could not copy"); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card sm:max-w-md" data-testid="welcome-card-dialog">
        <DialogHeader><DialogTitle className="font-head">Staff Welcome Card</DialogTitle></DialogHeader>
        {user && (
          <>
            <div className="overflow-hidden rounded-2xl border-2 border-primary" data-testid="welcome-card">
              <div className="flex items-center gap-2 bg-primary px-5 py-4 text-primary-foreground">
                <Zap className="h-5 w-5" /><div><p className="font-head text-lg font-bold leading-none">Connect Tym CRM</p><p className="text-xs opacity-80">Staff Welcome Card</p></div>
              </div>
              <div className="space-y-3 p-5">
                <Field l="Name" v={user.name} />
                <Field l="Role" v={`${user.role}${user.designation ? ` · ${user.designation}` : ""}`} />
                <Field l="Login ID" v={<span className="font-mono text-xl text-primary" data-testid="welcome-card-login-id">{user.login_id}</span>} />
                <Field l="Login at" v={<span className="break-all text-sm" data-testid="welcome-card-url">{crmUrl()}</span>} />
                <div className="flex flex-col items-center gap-1 pt-1" data-testid="welcome-card-qr">
                  <div className="rounded-lg bg-white p-2"><QRCodeSVG id="welcome-qr" value={crmUrl()} size={132} level="M" /></div>
                  <p className="text-[11px] text-muted-foreground">Scan to open the login page</p>
                </div>
                <p className="border-t border-dashed border-border pt-3 text-[11px] text-muted-foreground">The password is never printed. Share it with the staff member separately.</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" onClick={() => printCard(user)} data-testid="welcome-card-print"><Printer className="mr-1 h-4 w-4" /> Print</Button>
              <Button asChild className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="welcome-card-whatsapp">
                <a href={waLink(user)} target="_blank" rel="noreferrer"><MessageCircle className="mr-1 h-4 w-4" /> WhatsApp</a>
              </Button>
              <Button variant="outline" onClick={copy} data-testid="welcome-card-copy"><Copy className="mr-1 h-4 w-4" /> Copy</Button>
            </div>
            {!user.mobile && <p className="text-[11px] text-muted-foreground">No mobile number saved, so WhatsApp will ask you to pick a contact.</p>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ l, v }) => (
  <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</p><div className="font-semibold">{v}</div></div>
);
